<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { MarIcon, type IconType } from '@nimling/components-nimtech'
import { theme, toggleTheme } from '@lib/store/theme'
import { meStore } from '@lib/store/me'
import { modeStore, signOut } from '@lib/store/session'

defineProps<{ collapsed: boolean }>()
const emit = defineEmits<{ toggleCollapsed: [] }>()

const route = useRoute()
const router = useRouter()

const standalone = computed(() => modeStore.value === 'standalone')
const userName = computed(() => {
  const me = meStore.value
  return me ? [me.first_name, me.last_name].filter(Boolean).join(' ') || me.name || me.email || 'user' : 'user'
})

const isActive = (href: string) => route.path === href || route.path.startsWith(href + '/')

const go = (event: MouseEvent, href: string) => {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
  event.preventDefault()
  router.push(href)
}

const nav: { href: string; icon: IconType; label: string }[] = [
  { href: '/connections', icon: 'webhooks', label: 'connections' },
  { href: '/operations', icon: 'send', label: 'operations' },
  { href: '/runs', icon: 'clock', label: 'runs' },
  { href: '/devices', icon: 'thermometer', label: 'devices' },
  { href: '/insights', icon: 'chart-line', label: 'insights' },
  { href: '/query', icon: 'database', label: 'query' },
  { href: '/rules', icon: 'bolt', label: 'rules' },
  { href: '/traffic', icon: 'chart-bar', label: 'traffic' },
  { href: '/presentations', icon: 'globe', label: 'presented api' },
  { href: '/credentials', icon: 'shield-check', label: 'credentials' }
]

const catalog: { href: string; icon: IconType; label: string }[] = [
  { href: '/connectors', icon: 'sdk', label: 'connectors' },
  { href: '/keys', icon: 'key', label: 'api keys' },
  { href: '/issuers', icon: 'shield', label: 'issuers' },
  { href: '/gateway', icon: 'sitemap', label: 'gateway' }
]
</script>

<template>
  <aside class="app-sidebar">
    <div class="sidebar-inner glass" :class="{ collapsed }">
      <div class="sidebar-header">
        <img class="sidebar-brand-icon" src="/connect.svg" alt="" style="inline-size: 1.75em; block-size: 1.75em" />
        <span class="sidebar-brand-text">connect</span>
      </div>

      <nav class="sidebar-nav-group" aria-label="pages">
        <a
          v-for="item in nav"
          :key="item.href"
          class="sidebar-nav-item"
          :class="{ active: isActive(item.href) }"
          :href="item.href"
          :aria-current="isActive(item.href) ? 'page' : undefined"
          @click="go($event, item.href)"
        >
          <span class="sidebar-nav-icon"><MarIcon :icon="item.icon" /></span>
          <span class="sidebar-nav-label">{{ item.label }}</span>
          <span></span>
        </a>
      </nav>

      <div class="sidebar-section"><span>catalog</span></div>
      <nav class="sidebar-list" aria-label="catalog">
        <a
          v-for="item in catalog"
          :key="item.href"
          class="sidebar-nav-item"
          :class="{ active: isActive(item.href) }"
          :href="item.href"
          :aria-current="isActive(item.href) ? 'page' : undefined"
          @click="go($event, item.href)"
        >
          <span class="sidebar-nav-icon"><MarIcon :icon="item.icon" /></span>
          <span class="sidebar-nav-label">{{ item.label }}</span>
          <span></span>
        </a>
      </nav>

      <div class="sidebar-bottom-controls">
        <button class="sidebar-nav-item" type="button" @click="toggleTheme">
          <span class="sidebar-nav-icon"><MarIcon :icon="theme === 'dark' ? 'sun' : 'moon'" /></span>
          <span class="sidebar-nav-label">{{ theme === 'dark' ? 'light mode' : 'dark mode' }}</span>
          <span></span>
        </button>
        <button v-if="standalone" class="sidebar-nav-item" type="button" @click="signOut">
          <span class="sidebar-nav-icon"><MarIcon icon="sign-out" /></span>
          <span class="sidebar-nav-label">sign out</span>
          <span></span>
        </button>
        <button class="sidebar-nav-item" type="button" :aria-expanded="!collapsed" @click="emit('toggleCollapsed')">
          <span class="sidebar-nav-icon"><MarIcon :icon="collapsed ? 'chevrons-right' : 'chevrons-left'" /></span>
          <span class="sidebar-nav-label">{{ collapsed ? 'expand menu' : 'collapse menu' }}</span>
          <span></span>
        </button>
      </div>

      <div class="sidebar-user-wrapper">
        <div class="sidebar-user">
          <span class="sidebar-nav-icon"><MarIcon icon="user" /></span>
          <span class="sidebar-user-info">
            <span class="sidebar-user-name">{{ userName }}</span>
            <span class="sidebar-user-role">{{ standalone ? 'standalone' : 'connect' }}</span>
          </span>
        </div>
      </div>
    </div>
  </aside>
</template>
